/* eslint-disable max-lines-per-function */
import { FieldBase } from '@/components/form';
import { useAppForm } from '@/components/form/formHooks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FieldDescription,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@e2n/ui';

import { useSchema } from '@/domain/vertraege/features/VertragKuendigen/useSchema';
import { useVertraegeKuendigen } from '@/domain/vertraege/shared/hooks/useVertraegeKuendigen';
import { KuendigungsGrund } from '@/domain/vertraege/shared/types/kuendigungsGrund';
import { Vertrag } from '@/domain/vertraege/shared/types/vertrag';
import { mapKuendigungsGrund } from '@/domain/vertraege/shared/utils/kuendigungsGrundMapper';
import { useProfile } from '@/hooks';
import { toast } from '@/hooks/use-toast';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

const MIN_KUENDIGUNGSFRIST_TAGE = 28;
const MONATLICHE_KUENDIGUNGSOPTIONEN = 6;
const JAEHRLICHE_KUENDIGUNGSOPTIONEN = 6;

function buildKuendigungsOptionen(vertrag: Vertrag) {
  const heute = dayjs().startOf('day');
  const hasMinimumNotice = (endDateIso: dayjs.Dayjs) =>
    endDateIso.endOf('day').diff(heute, 'day') >= MIN_KUENDIGUNGSFRIST_TAGE;

  if (vertrag.vertragIntervall === 'JAHR') {
    let erstesMoeglichesVertragsende = vertrag.faelligkeitsdatum
      ? dayjs(vertrag.faelligkeitsdatum).endOf('day')
      : dayjs().endOf('year');

    if (!erstesMoeglichesVertragsende.isValid()) {
      erstesMoeglichesVertragsende = dayjs().endOf('year');
    }

    while (!hasMinimumNotice(erstesMoeglichesVertragsende)) {
      erstesMoeglichesVertragsende = erstesMoeglichesVertragsende
        .add(1, 'year')
        .endOf('day');
    }

    return Array.from(
      { length: JAEHRLICHE_KUENDIGUNGSOPTIONEN },
      (_, index) => {
        const vertragsende = erstesMoeglichesVertragsende
          .add(index, 'year')
          .endOf('day');

        return {
          label: vertragsende.format('DD.MM.YYYY'),
          value: vertragsende.toISOString(),
        };
      },
    );
  }

  let erstesMoeglichesMonatsende = dayjs().endOf('month');

  while (!hasMinimumNotice(erstesMoeglichesMonatsende)) {
    erstesMoeglichesMonatsende = erstesMoeglichesMonatsende
      .add(1, 'month')
      .endOf('month');
  }

  return Array.from({ length: MONATLICHE_KUENDIGUNGSOPTIONEN }, (_, index) => {
    const vertragsende = erstesMoeglichesMonatsende
      .add(index, 'month')
      .endOf('month');

    return {
      label: vertragsende.format('DD.MM.YYYY'),
      value: vertragsende.toISOString(),
    };
  });
}

// oxlint-disable-next-line complexity
export function VertragKuendigen({ vertrag }: { vertrag: Vertrag }) {
  const { t } = useTranslation();
  const { manager } = useProfile();
  const [vertraegeKuendigen] = useVertraegeKuendigen();
  const [open, setOpen] = useState(false);
  const kuendigungsOptionen = buildKuendigungsOptionen(vertrag);

  const formSchema = useSchema({ t });

  const form = useAppForm({
    defaultValues: {
      vorname: manager?.vorname || '',
      nachname: manager?.nachname || '',
      kundennummer: 'K-' + vertrag.kundennummer.toString(),
      vertragsnummer: 'W-' + vertrag.id.toString(),
      unternehmen: vertrag.unternehmensname,
      email: manager?.email || '',
      telefon: '',
      grund: '',
      kuendigungZum: undefined,
      beschreibung: '',
    } satisfies z.infer<typeof formSchema> as z.infer<typeof formSchema>,
    validators: {
      onSubmit: formSchema,
    },
    async onSubmit({ value }) {
      const grund = Object.values(KuendigungsGrund).find(
        (g) => g === value.grund,
      );
      if (!value.kuendigungZum || !grund) {
        return;
      }
      try {
        const result = await vertraegeKuendigen({
          input: {
            beschreibung: value.beschreibung,
            kuendigungZum: value.kuendigungZum,
            kuendigungVersendetAm: dayjs().toISOString(),
            telefon: value.telefon,
            grund: mapKuendigungsGrund(grund),
            vertragsId: value.vertragsnummer.split('-')[1],
            kundennummer: value.kundennummer.split('-')[1],
            nachname: value.nachname,
            vorname: value.vorname,
            unternehmen: value.unternehmen,
          },
        });
        if (result.data?.vertraege_kuendigen === true) {
          toast({
            description: t('domain.vertraege.kuendigenForm.submit.success'),
          });
          setOpen(false);
          form.reset();
        } else {
          toast({
            variant: 'destructive',
            description: t('domain.vertraege.kuendigenForm.submit.error'),
          });
        }
      } catch {
        toast({
          variant: 'destructive',
          description: t('domain.vertraege.kuendigenForm.submit.error'),
        });
      }
    },
  });

  const dialogTrigger =
    vertrag.kuendigung !== null ? (
      <Tooltip>
        <TooltipTrigger>
          <Button
            variant="outline-secondary"
            disabled={vertrag.kuendigung !== null}>
            {t('domain.vertraege.kuendigung.button')}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {t('domain.vertraege.kuendigung.tooltip', {
            gekuendigtVon: vertrag.kuendigung.gekuendigtVon,
            gekuendigtAm: dayjs(vertrag.kuendigung.gekuendigtAm).format(
              'DD.MM.YYYY',
            ),
          })}
        </TooltipContent>
      </Tooltip>
    ) : (
      <Button
        variant="outline-secondary"
        disabled={vertrag.kuendigung !== null}>
        {t('domain.vertraege.kuendigung.button')}
      </Button>
    );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{dialogTrigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('domain.vertraege.kuendigenForm.title')}</DialogTitle>
        </DialogHeader>
        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-6 mt-4">
            <div className="flex gap-4 w-full">
              <form.AppField name="vorname">
                {(field) => (
                  <field.TextInput
                    disabled
                    label={t(
                      'domain.vertraege.kuendigenForm.fields.vorname.label',
                    )}
                  />
                )}
              </form.AppField>
              <form.AppField name="nachname">
                {(field) => (
                  <field.TextInput
                    disabled
                    label={t(
                      'domain.vertraege.kuendigenForm.fields.nachname.label',
                    )}
                  />
                )}
              </form.AppField>
            </div>
            <form.AppField name="unternehmen">
              {(field) => (
                <field.TextInput
                  disabled
                  label={t(
                    'domain.vertraege.kuendigenForm.fields.unternehmen.label',
                  )}
                />
              )}
            </form.AppField>

            <div className="flex gap-4 w-full">
              <form.AppField name="kundennummer">
                {(field) => (
                  <field.TextInput
                    disabled
                    label={t(
                      'domain.vertraege.kuendigenForm.fields.kundennummer.label',
                    )}
                  />
                )}
              </form.AppField>
              <form.AppField name="vertragsnummer">
                {(field) => (
                  <field.TextInput
                    disabled
                    label={t(
                      'domain.vertraege.kuendigenForm.fields.vertragsnummer.label',
                    )}
                  />
                )}
              </form.AppField>
            </div>
            <div className="flex gap-4 w-full items-start">
              <form.AppField name="email">
                {(field) => (
                  <field.TextInput
                    disabled
                    label={t(
                      'domain.vertraege.kuendigenForm.fields.email.label',
                    )}
                  />
                )}
              </form.AppField>
              <form.AppField name="telefon">
                {(field) => (
                  <field.TextInput
                    isRequired
                    placeholder={t(
                      'domain.vertraege.kuendigenForm.fields.telefon.placeholder',
                    )}
                    label={t(
                      'domain.vertraege.kuendigenForm.fields.telefon.label',
                    )}
                  />
                )}
              </form.AppField>
            </div>
            <div className="flex gap-4 w-full items-start">
              <form.AppField name="grund">
                {(field) => (
                  <FieldBase
                    isRequired
                    label={t(
                      'domain.vertraege.kuendigenForm.fields.grund.label',
                    )}>
                    <Select
                      value={field.state.value}
                      onValueChange={(value) => field.handleChange(value)}>
                      <SelectTrigger
                        className={`w-full border-neutrals-300 ${
                          !field.state.value
                            ? 'text-neutrals-700'
                            : 'text-neutrals-900'
                        }`}>
                        <SelectValue placeholder="Grund auswählen">
                          {field.state.value
                            ? t(
                                `domain.vertraege.kuendigenForm.fields.grund.select.${field.state.value}`,
                              )
                            : undefined}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="border-neutrals-300">
                        <SelectGroup>
                          {Object.values(KuendigungsGrund).map((enumValue) => (
                            <SelectItem key={enumValue} value={enumValue}>
                              {t(
                                `domain.vertraege.kuendigenForm.fields.grund.select.${enumValue}`,
                              )}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FieldBase>
                )}
              </form.AppField>
              <div className="flex flex-col gap-1 w-full">
                <form.AppField name="kuendigungZum">
                  {(field) => (
                    <FieldBase
                      isRequired
                      label={t(
                        'domain.vertraege.kuendigenForm.fields.kuendigungZum.label',
                      )}>
                      <Select
                        value={field.state.value || ''}
                        onValueChange={(value) => field.handleChange(value)}>
                        <SelectTrigger
                          className={`w-full border-neutrals-300 ${
                            !field.state.value
                              ? 'text-neutrals-700'
                              : 'text-neutrals-900'
                          }`}>
                          <SelectValue placeholder={t('combobox.placeholder')}>
                            {
                              kuendigungsOptionen.find(
                                (option) => option.value === field.state.value,
                              )?.label
                            }
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="border-neutrals-300">
                          <SelectGroup>
                            {kuendigungsOptionen.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FieldBase>
                  )}
                </form.AppField>
                <FieldDescription className="text-xs text-neutrals-700">
                  {t(
                    'domain.vertraege.kuendigenForm.fields.kuendigungZum.description',
                  )}
                </FieldDescription>
              </div>
            </div>
            <form.AppField name="beschreibung">
              {(field) => (
                <field.TextArea
                  label={t(
                    'domain.vertraege.kuendigenForm.fields.beschreibung.label',
                  )}
                />
              )}
            </form.AppField>
            <div className="flex justify-end mt-4">
              <form.SubmitButton
                buttonText={t('domain.vertraege.kuendigenForm.submit.label')}
              />
            </div>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}
